import { faPercent } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputAdornment from '@mui/material/InputAdornment';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { useFieldContext } from '~/common/form/formContexts';

import Input from './InputBase/Input';

type MuiInputType = Omit<
  React.ComponentProps<typeof Input>,
  'helperText' | 'defaultValue'
>;

type InputProps = Partial<React.ComponentProps<typeof NumericFormat>> &
  MuiInputType & {
    min?: number;
    max?: number;
    errorMessage?: string;
    defaultValue?: string | number;
    useReactHooksForm?: boolean;
    useTanstackForm?: boolean;
  };

const PercentInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<InputProps>
>(({ useReactHooksForm = false, useTanstackForm = false, ...props }, ref) => {
  if (useReactHooksForm) {
    return <HookFormInput {...props} />;
  }
  if (useTanstackForm) {
    return <TanstackFormInput {...props} ref={ref} />;
  }
  return <DefaultInput {...props} ref={ref} />;
});

const HookFormInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<InputProps>
>(({ name, ...props }, ref) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={(hooksProps, { invalid }) => {
        return (
          <DefaultInput
            error={invalid}
            errorMessage={errors[name]?.message}
            {...props}
            {...hooksProps}
            ref={ref}
          />
        );
      }}
    />
  );
});

const DefaultInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<InputProps>
>(
  (
    {
      onFocus,
      InputProps,
      width = '100%',
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      error,
      errorMessage,
      ...props
    },
    ref,
  ) => {
    return (
      <NumericFormat<MuiInputType>
        width={width}
        getInputRef={ref}
        customInput={Input}
        error={error}
        isAllowed={({ floatValue }) => {
          if (floatValue === undefined) return true;
          if (floatValue < max && floatValue > min) return true;
          return false;
        }}
        InputProps={{
          onFocus,
          endAdornment: (
            <InputAdornment position="end">
              <FontAwesomeIcon
                icon={faPercent}
                style={{ fontSize: '0.75rem' }}
              />
            </InputAdornment>
          ),
          ...InputProps,
        }}
        errorMessage={errorMessage}
        {...props}
      />
    );
  },
);

const TanstackFormInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<InputProps>
>(({ ...props }, ref) => {
  const field = useFieldContext<number | undefined>();
  const errorMessage = field.state.meta.errors[0] as string | undefined;
  return (
    <DefaultInput
      {...props}
      ref={ref}
      value={field.state.value as any}
      error={field.state.meta.errors.length > 0}
      errorMessage={errorMessage}
      onBlur={field.handleBlur}
    />
  );
});

export default PercentInput;
